// Usuários Admin/Vendedor persistem no MongoDB via API (Etapa 2) — senhas com hash bcrypt
// no servidor, nunca em texto puro nem retornadas pelas rotas. A sessão do painel continua
// no navegador (UX preservada); credenciais e dados são validados no backend.
import { useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const EVENT = 'hud-users-changed';

let usersCache = [];
let usersReady = false;
let initPromise = null;

const notify = () => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(EVENT));
};

const staffRole = () => {
  try {
    return JSON.parse(localStorage.getItem('hud_admin_user') || 'null')?.role || '';
  } catch {
    return '';
  }
};

async function api(path, options = {}) {
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', 'X-Admin-Role': staffRole(), ...(options.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.detail || 'Falha na operação.'), { status: res.status, detail: data.detail });
  return data;
}

// Lista de usuários é interna (painel); sem sessão de staff, mantém cache vazio.
export function initUsers(force = false) {
  if (initPromise && !force) return initPromise;
  if (!staffRole()) return Promise.resolve();
  initPromise = (async () => {
    try {
      usersCache = await api('/api/users');
      usersReady = true;
      notify();
    } catch (e) {
      console.error('Usuários: falha ao carregar do servidor.', e);
    }
  })();
  return initPromise;
}

export function useUsersTick() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const h = () => setTick((t) => t + 1);
    window.addEventListener(EVENT, h);
    initUsers();
    return () => window.removeEventListener(EVENT, h);
  }, []);
}

// Leituras síncronas do cache hidratado pelo servidor.
export const loadUsers = () => usersCache;
export const getUser = (id) => usersCache.find((u) => u.id === id) || null;
export const getSellers = () => usersCache.filter((u) => u.role === 'vendedor');

export const roleLabel = (role) => (role === 'admin' ? 'Administrador(a)' : 'Vendedor(a)');

// Login validado no servidor (bcrypt). A regra de horário do vendedor é aplicada
// pelo chamador (QueueContext) DEPOIS de identificar usuário e perfil.
export async function authenticate(login, password) {
  try {
    const res = await fetch(`${API}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ login, password }),
    });
    if (res.status === 401) return { ok: false, reason: 'credenciais' };
    if (res.status === 403) return { ok: false, reason: 'inativo' };
    if (!res.ok) return { ok: false, reason: 'credenciais' };
    const user = await res.json();
    return { ok: true, user };
  } catch {
    return { ok: false, reason: 'credenciais' };
  }
}

export async function createUser({ name, login, password, role, active = true }) {
  const cleanLogin = String(login || '').trim().toLowerCase();
  if (!name?.trim() || !cleanLogin || !password) return { ok: false, reason: 'campos' };
  try {
    const user = await api('/api/users', {
      method: 'POST',
      body: JSON.stringify({ name, login: cleanLogin, password, role, active }),
    });
    usersCache = [...usersCache, user];
    notify();
    return { ok: true, user };
  } catch (e) {
    return { ok: false, reason: e.detail === 'login_existe' ? 'login_existe' : 'campos' };
  }
}

export async function updateUser(id, patch) {
  await api(`/api/users/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(patch) });
  usersCache = usersCache.map((u) => (u.id === id ? { ...u, ...patch } : u));
  notify();
}

export async function resetPassword(id, newPassword) {
  await api(`/api/users/${encodeURIComponent(id)}/password`, {
    method: 'PUT',
    body: JSON.stringify({ password: newPassword }),
  });
}

// Exclusão definitiva (admin only): o servidor bloqueia autoexclusão e a remoção do
// último admin ativo. X-Admin-User identifica o chamador para a regra de autoexclusão.
export async function deleteUser(id) {
  const me = JSON.parse(localStorage.getItem('hud_admin_user') || 'null');
  await api(`/api/users/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: { 'X-Admin-User': me?.id || '' },
  });
  usersCache = usersCache.filter((u) => u.id !== id);
  notify();
}

// Artigo do vendedor no cabeçalho da mensagem de WhatsApp ("Aqui é o/a ..."). Não existe
// campo de gênero no cadastro — mapa explícito por id de usuário (solução mínima, sem
// mudar banco/API; usuário novo cai no padrão 'o' até ser incluído aqui). Jamais inferir
// gênero pelo formato do nome.
const USER_ARTICLES = { leandro: 'o', paula: 'a', joao: 'o', fernanda: 'a', carlos: 'o' };
export const getUserArticle = (id) => USER_ARTICLES[String(id || '').toLowerCase()] || 'o';

export const ADMIN_REASONS = [
  'Vendedor faltou',
  'Vendedor encerrou expediente',
  'Atribuição feita por engano',
  'Problema operacional',
  'Orientação da gerência',
  'Outro',
];
